<template>
  <div class="client-details-view">
    <!-- Back Button -->
    <div class="client-details-view-header">
      <button class="client-details-back-btn" @click="$emit('back')">
        <i class="bi bi-arrow-left"></i>
        Back to Clients
      </button>
    </div>
    
    <div v-if="loading" class="client-details-loading">
      <div class="client-details-spinner"></div>
      <p>Loading client details...</p>
    </div>
    
    <div v-else-if="error" class="client-details-error">
      <i class="bi bi-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button class="client-details-btn client-details-btn-primary" @click="loadClientDetails">
        Retry
      </button>
    </div>
    
    <div v-else-if="clientDetails" class="client-details-body">
      <!-- Client Information Section -->
      <div class="client-details-section">
        <h4 class="client-details-section-title">
          <i class="bi bi-person"></i> Client Information
        </h4>
        <div class="client-details-info-grid">
          <div class="client-details-info-item">
            <label>Name</label>
            <p>{{ clientDetails.firstName }} {{ clientDetails.lastName }}</p>
          </div>
          <div class="client-details-info-item">
            <label>Email</label>
            <p>{{ clientDetails.email }}</p>
          </div>
          <div class="client-details-info-item">
            <label>Company Name</label>
            <p>{{ clientDetails.companyName }}</p>
          </div>
          <div class="client-details-info-item">
            <label>Company Website</label>
            <p>
              <a :href="clientDetails.companyWebsite" target="_blank" rel="noopener noreferrer">
                {{ clientDetails.companyWebsite }}
              </a>
            </p>
          </div>
          <div class="client-details-info-item">
            <label>Status</label>
            <p>
              <span :class="['client-details-status-badge', clientDetails.is_active ? 'active' : 'inactive']">
                {{ clientDetails.is_active ? 'Active' : 'Inactive' }}
              </span>
            </p>
          </div>
          <div class="client-details-info-item" v-if="clientDetails.manager">
            <label>Assigned Manager</label>
            <p>{{ clientDetails.manager.firstName }} {{ clientDetails.manager.lastName }}</p>
          </div>
          <div class="client-details-info-item">
            <label>Created At</label>
            <p>{{ formatDate(clientDetails.createdAt) }}</p>
          </div>
        </div>
      </div>
      
      <!-- Overall Statistics Section -->
      <div class="client-details-section">
        <h4 class="client-details-section-title">
          <i class="bi bi-bar-chart"></i> Overall Statistics
        </h4>
        <div class="client-details-stats-grid">
          <div class="client-details-stat-card">
            <div class="client-details-stat-icon client-details-stat-icon-blue">
              <i class="bi bi-building"></i>
            </div>
            <div class="client-details-stat-content">
              <h3 class="client-details-stat-value">{{ clientDetails.stats?.totalCompanies || 0 }}</h3>
              <p class="client-details-stat-label">Companies</p>
            </div>
          </div>
          <div class="client-details-stat-card">
            <div class="client-details-stat-icon client-details-stat-icon-green">
              <i class="bi bi-people"></i>
            </div>
            <div class="client-details-stat-content">
              <h3 class="client-details-stat-value">{{ clientDetails.stats?.totalCustomers || 0 }}</h3>
              <p class="client-details-stat-label">Total Customers</p>
            </div>
          </div>
          <div class="client-details-stat-card">
            <div class="client-details-stat-icon client-details-stat-icon-purple">
              <i class="bi bi-truck"></i>
            </div>
            <div class="client-details-stat-content">
              <h3 class="client-details-stat-value">{{ clientDetails.stats?.totalVendors || 0 }}</h3>
              <p class="client-details-stat-label">Total Vendors</p>
            </div>
          </div>
          <div class="client-details-stat-card">
            <div class="client-details-stat-icon client-details-stat-icon-orange">
              <i class="bi bi-person-badge"></i>
            </div>
            <div class="client-details-stat-content">
              <h3 class="client-details-stat-value">{{ clientDetails.stats?.totalConsultants || 0 }}</h3>
              <p class="client-details-stat-label">Total Consultants</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Companies Section -->
      <div class="client-details-section" v-if="clientDetails.companies && clientDetails.companies.length > 0">
        <h4 class="client-details-section-title">
          <i class="bi bi-building"></i> Companies ({{ clientDetails.companies.length }})
        </h4>
        <div class="client-details-companies-layout">
          <ClientCompanyRecordsNav
            v-if="detailsSource === 'admin' && companySideMenuOpen && sideMenuCompany"
            :details-source="detailsSource"
            :client-id="clientId"
            :company="sideMenuCompany"
            variant="inline"
            :show-close="true"
            @close="closeCompanySideMenu"
            @navigate="closeCompanySideMenu"
          />
          <div class="client-details-companies-list-wrap">
            <div class="client-details-companies-list">
              <div
                v-for="company in clientDetails.companies"
                :key="company.id"
                class="client-details-company-card"
                :class="{
                  'client-details-company-card--clickable': detailsSource === 'admin',
                  'client-details-company-card--selected':
                    detailsSource === 'admin' &&
                    companySideMenuOpen &&
                    sideMenuCompany &&
                    sideMenuCompany.id === company.id
                }"
                :role="detailsSource === 'admin' ? 'button' : undefined"
                :tabindex="detailsSource === 'admin' ? 0 : undefined"
                :aria-label="detailsSource === 'admin' ? `Open menu for ${company.name}` : undefined"
                @click="onCompanyCardActivate(company)"
                @keydown.enter.prevent="onCompanyCardActivate(company)"
                @keydown.space.prevent="onCompanyCardActivate(company)"
              >
                <div
                  v-if="detailsSource === 'manager' && canAccessCompanyRecords"
                  class="client-details-company-card-manager-open"
                  @click.stop
                >
                  <label class="client-details-company-manager-open-label" :for="managerRecordsSelectId(company)">
                    Open in manager
                  </label>
                  <select
                    :id="managerRecordsSelectId(company)"
                    class="client-details-company-manager-open-select"
                    aria-label="Choose Timesheets, Invoices, or Bills"
                    @change="onManagerCompanyRecordsDestination(company, $event)"
                  >
                    <option value="" selected disabled>Select…</option>
                    <option value="ManagerCompanyTimesheets">Timesheets</option>
                    <option value="ManagerCompanyInvoices">Invoices</option>
                    <option value="ManagerCompanyBills">Bills</option>
                  </select>
                </div>
                <div v-else-if="detailsSource === 'admin'" class="client-details-company-card-hint">
                  <i class="bi bi-layout-text-sidebar"></i> Show company menu
                </div>
                <div class="client-details-company-header">
                  <div class="client-details-company-title">
                    <h5 class="client-details-company-name">{{ company.name }}</h5>
                    <p class="client-details-company-key" v-if="company.legalName">{{ company.legalName }}</p>
                  </div>
                  <div class="client-details-company-stats">
                    <div class="client-details-company-stat-item">
                      <span class="client-details-company-stat-value">{{ company.stats?.totalCustomers || 0 }}</span>
                      <span class="client-details-company-stat-label">Customers</span>
                    </div>
                    <div class="client-details-company-stat-item">
                      <span class="client-details-company-stat-value">{{ company.stats?.totalVendors || 0 }}</span>
                      <span class="client-details-company-stat-label">Vendors</span>
                    </div>
                    <div class="client-details-company-stat-item">
                      <span class="client-details-company-stat-value">{{ company.stats?.totalConsultants || 0 }}</span>
                      <span class="client-details-company-stat-label">Consultants</span>
                    </div>
                  </div>
                </div>
                <div class="client-details-company-details">
                  <div class="client-details-company-info-grid">
                    <div class="client-details-company-info-item" v-if="company.email">
                      <label>Email</label>
                      <p>{{ company.email }}</p>
                    </div>
                    <div class="client-details-company-info-item" v-if="company.phone">
                      <label>Phone</label>
                      <p>{{ company.phone }}</p>
                    </div>
                    <div class="client-details-company-info-item" v-if="company.website">
                      <label>Website</label>
                      <p>
                        <a
                          :href="company.website"
                          target="_blank"
                          rel="noopener noreferrer"
                          @click.stop
                        >
                          {{ company.website }}
                        </a>
                      </p>
                    </div>
                    <div class="client-details-company-info-item" v-if="company.addressLine1 || company.city">
                      <label>Address</label>
                      <p>
                        <span v-if="company.addressLine1">{{ company.addressLine1 }}</span>
                        <span v-if="company.city">, {{ company.city }}</span>
                        <span v-if="company.country">, {{ company.country }}</span>
                      </p>
                    </div>
                    <div class="client-details-company-info-item" v-if="company.createdAt">
                      <label>Created At</label>
                      <p>{{ formatDate(company.createdAt) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else-if="clientDetails.companies && clientDetails.companies.length === 0" class="client-details-empty-companies">
        <i class="bi bi-building"></i>
        <p>No companies found for this client.</p>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import ClientCompanyRecordsNav from '@/components/ClientCompanyRecordsNav.vue';
import './ClientDetailsView.css';

export default {
  name: 'ClientDetailsView',
  components: { ClientCompanyRecordsNav },
  props: {
    clientId: {
      type: Number,
      required: true
    },
    /** 'admin' = links to /company/... dashboard; 'manager' = dedicated manager record pages */
    detailsSource: {
      type: String,
      default: 'admin',
      validator: (v) => v === 'admin' || v === 'manager'
    }
  },
  emits: ['back'],
  data() {
    return {
      clientDetails: null,
      loading: false,
      error: null,
      companySideMenuOpen: false,
      sideMenuCompany: null
    }
  },
  computed: {
    canAccessCompanyRecords() {
      if (this.detailsSource !== 'manager') return false;
      if (this.clientDetails && typeof this.clientDetails.canAccessCompanyRecords === 'boolean') {
        return this.clientDetails.canAccessCompanyRecords;
      }
      try {
        const managerUser = JSON.parse(localStorage.getItem('managerUser') || 'null');
        if (managerUser && typeof managerUser.canAccessCompanyRecords === 'boolean') {
          return managerUser.canAccessCompanyRecords;
        }
        if (managerUser && managerUser.isDefaultManager === true) {
          return false;
        }
      } catch (_) {
        /* ignore */
      }
      return true;
    }
  },
  watch: {
    clientId: {
      immediate: true,
      handler() {
        if (this.clientId) {
          this.loadClientDetails();
        }
      }
    },
    detailsSource() {
      if (this.clientId) {
        this.loadClientDetails();
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onCompanyDrawerKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onCompanyDrawerKeydown);
  },
  methods: {
    onCompanyCardActivate(company) {
      if (!company || company.id == null) return;
      if (this.detailsSource === 'manager') {
        return;
      }
      this.openCompanySideMenu(company);
    },
    managerRecordsSelectId(company) {
      return `manager-company-records-${this.clientId}-${company.id}`;
    },
    onManagerCompanyRecordsDestination(company, event) {
      const routeName = event?.target?.value;
      if (!routeName || !company || company.id == null) return;
      this.$router.push({
        name: routeName,
        params: {
          clientId: String(this.clientId),
          companyId: String(company.id)
        },
        query: { companyName: company.name || '' }
      });
      event.target.value = '';
    },
    openCompanySideMenu(company) {
      if (!company || company.id == null) return;
      this.sideMenuCompany = company;
      this.companySideMenuOpen = true;
    },
    closeCompanySideMenu() {
      this.companySideMenuOpen = false;
      this.sideMenuCompany = null;
    },
    onCompanyDrawerKeydown(e) {
      if (e.key === 'Escape' && this.companySideMenuOpen) {
        this.closeCompanySideMenu();
      }
    },
    async loadClientDetails() {
      if (!this.clientId) {
        this.error = 'Client ID is required';
        return;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        const path =
          this.detailsSource === 'manager'
            ? `${process.env.VUE_APP_API_URL}/client-manager/clients/${this.clientId}/details`
            : `${process.env.VUE_APP_API_URL}/anna-admin/clients/${this.clientId}`;
        const response = await axios.get(path);
        this.clientDetails = response.data;
        if (
          this.detailsSource === 'manager' &&
          typeof response.data?.canAccessCompanyRecords === 'boolean'
        ) {
          try {
            const managerUser = JSON.parse(localStorage.getItem('managerUser') || 'null') || {};
            managerUser.canAccessCompanyRecords = response.data.canAccessCompanyRecords;
            managerUser.isDefaultManager = response.data.canAccessCompanyRecords === false;
            localStorage.setItem('managerUser', JSON.stringify(managerUser));
          } catch (_) {
            /* ignore */
          }
        }
      } catch (error) {
        console.error('Error loading client details:', error);
        this.error = error.response?.data?.message || 'Failed to load client details. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
}
</script>
